import { useState, useEffect } from "react";
import { db } from "../../firebase/config";
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc } from "firebase/firestore";

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchUser, setSearchUser] = useState("");
  const [searchProduct, setSearchProduct] = useState("");
  const [newProduct, setNewProduct] = useState({ name: "", price: "", description: "", image: "" });
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [currentPageUsers, setCurrentPageUsers] = useState(1);
  const [currentPageProducts, setCurrentPageProducts] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setFilteredUsers(
      users.filter((user) =>
        user.displayName?.toLowerCase().includes(searchUser.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchUser.toLowerCase())
      )
    );
    setFilteredProducts(
      products.filter((product) =>
        product.name?.toLowerCase().includes(searchProduct.toLowerCase())
      )
    );
  }, [users, products, searchUser, searchProduct]);

  const fetchData = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, "users"));
      const productsSnapshot = await getDocs(collection(db, "products"));

      setUsers(usersSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      setProducts(productsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, "products"), {
        ...newProduct,
        price: parseFloat(newProduct.price),
        createdAt: new Date().toISOString(),
      });
      setNewProduct({ name: "", price: "", description: "", image: "" });
      setShowAddForm(false);
      fetchData();
    } catch (error) {
      console.error("Error adding product:", error);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await deleteDoc(doc(db, "products", productId));
        fetchData();
      } catch (error) {
        console.error("Error deleting product:", error);
      }
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    try {
      await updateDoc(doc(db, "products", editingProduct.id), {
        name: editingProduct.name,
        price: parseFloat(editingProduct.price),
        description: editingProduct.description,
        image: editingProduct.image,
      });
      setEditingProduct(null);
      fetchData();
    } catch (error) {
      console.error("Error updating product:", error);
    }
  };

  const paginate = (data, currentPage) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <h1 className="text-center text-3xl font-bold mb-6 text-blue-600">Admin Dashboard</h1>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Users Section */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Users</h2>
          <input
            type="text"
            placeholder="Search users..."
            className="mb-4 w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:ring-blue-300"
            value={searchUser}
            onChange={(e) => setSearchUser(e.target.value)}
          />
          {paginate(filteredUsers, currentPageUsers).map((user) => (
            <div key={user.id} className="p-3 border rounded flex justify-between items-center bg-gray-50">
              <div>
                <p className="font-medium">{user.displayName || user.email}</p>
                <p className="text-sm text-gray-600">{user.email}</p>
              </div>
              <button
                onClick={() => setSelectedUser(user)}
                className="text-blue-500 underline"
              >
                View
              </button>
            </div>
          ))}
          <div className="mt-4 flex justify-end space-x-2">
            <button
              onClick={() => setCurrentPageUsers((prev) => Math.max(prev - 1, 1))}
              className="px-3 py-1 bg-gray-300 rounded"
              disabled={currentPageUsers === 1}
            >
              Prev
            </button>
            <button
              onClick={() =>
                setCurrentPageUsers((prev) =>
                  prev * itemsPerPage < filteredUsers.length ? prev + 1 : prev
                )
              }
              className="px-3 py-1 bg-gray-300 rounded"
              disabled={currentPageUsers * itemsPerPage >= filteredUsers.length}
            >
              Next
            </button>
          </div>
        </div>

        {/* Products Section */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Products</h2>
            <button
              onClick={() => setShowAddForm((prev) => !prev)}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
            >
              {showAddForm ? "Close Form" : "Add Product"}
            </button>
          </div>
          <input
            type="text"
            placeholder="Search products..."
            className="mb-4 w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:ring-blue-300"
            value={searchProduct}
            onChange={(e) => setSearchProduct(e.target.value)}
          />
          {showAddForm && (
            <form onSubmit={handleAddProduct} className="mb-6 p-4 border rounded bg-gray-50 shadow">
              <h3 className="font-medium mb-3">Add New Product</h3>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Product Name"
                  className="w-full px-3 py-2 border rounded"
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                  required
                />
                <input
                  type="number"
                  placeholder="Price"
                  className="w-full px-3 py-2 border rounded"
                  value={newProduct.price}
                  onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                  required
                />
                <input
                  type="text"
                  placeholder="Description"
                  className="w-full px-3 py-2 border rounded"
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                  required
                />
                <input
                  type="text"
                  placeholder="Image URL"
                  className="w-full px-3 py-2 border rounded"
                  value={newProduct.image}
                  onChange={(e) => setNewProduct({ ...newProduct, image: e.target.value })}
                  required
                />
                <div className="flex justify-end">
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                  >
                    Add Product
                  </button>
                </div>
              </div>
            </form>
          )}
          {paginate(filteredProducts, currentPageProducts).map((product) => (
            <div
              key={product.id}
              className="p-3 border rounded mb-3 flex justify-between items-center bg-gray-50 shadow"
            >
              {editingProduct?.id === product.id ? (
                <form onSubmit={handleUpdateProduct} className="space-y-2 w-full">
                  <input
                    type="text"
                    placeholder="Product Name"
                    className="w-full px-2 py-1 border rounded"
                    value={editingProduct.name}
                    onChange={(e) =>
                      setEditingProduct({ ...editingProduct, name: e.target.value })
                    }
                    required
                  />
                  <input
                    type="number"
                    placeholder="Price"
                    className="w-full px-2 py-1 border rounded"
                    value={editingProduct.price}
                    onChange={(e) =>
                      setEditingProduct({ ...editingProduct, price: e.target.value })
                    }
                    required
                  />
                  <input
                    type="text"
                    placeholder="Description"
                    className="w-full px-2 py-1 border rounded"
                    value={editingProduct.description}
                    onChange={(e) =>
                      setEditingProduct({ ...editingProduct, description: e.target.value })
                    }
                  />
                  <input
                    type="text"
                    placeholder="Image URL"
                    className="w-full px-2 py-1 border rounded"
                    value={editingProduct.image}
                    onChange={(e) =>
                      setEditingProduct({ ...editingProduct, image: e.target.value })
                    }
                  />
                  <div className="flex justify-end space-x-2 mt-2">
                    <button
                      type="submit"
                      className="px-4 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingProduct(null)}
                      className="px-4 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <div>
                    <p className="font-medium">{product.name}</p>
                    <p className="text-sm text-gray-600">${product.price}</p>
                    <p className="text-sm text-gray-500">{product.description}</p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setEditingProduct(product)}
                      className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteProduct(product.id)}
                      className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
          <div className="mt-4 flex justify-end space-x-2">
            <button
              onClick={() => setCurrentPageProducts((prev) => Math.max(prev - 1, 1))}
              className="px-3 py-1 bg-gray-300 rounded"
              disabled={currentPageProducts === 1}
            >
              Prev
            </button>
            <button
              onClick={() =>
                setCurrentPageProducts((prev) =>
                  prev * itemsPerPage < filteredProducts.length ? prev + 1 : prev
                )
              }
              className="px-3 py-1 bg-gray-300 rounded"
              disabled={currentPageProducts * itemsPerPage >= filteredProducts.length}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {selectedUser && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-sm">
            <h3 className="text-xl font-medium mb-4">User Details</h3>
            <p><strong>Name:</strong> {selectedUser.displayName || "N/A"}</p>
            <p><strong>Email:</strong> {selectedUser.email}</p>
            {selectedUser.isAdmin && <p><strong>Role:</strong> Admin</p>}
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setSelectedUser(null)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
